import React, { useState, useEffect } from "react";
import backgroundCover from "../../../assets/learningBackground.png";
import Mask from "../../../assets/dots-rectangle.svg";
import { motion } from "framer-motion";
import axios from "axios";

const FAQ = () => {
  const [faqData, setFaqData] = useState({});
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    location: "",
    trainingTopics: "",
    additionalNotes: "",
  });
  const [focusedField, setFocusedField] = useState("");

  useEffect(() => {
    const getNavData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/Form`,
        );
        setFaqData(response?.data?.data[0]);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    getNavData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    // Add your form submission logic here
  };

  const backgroundStyle = {
    backgroundImage: `url(${backgroundCover})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    zIndex: "0",
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const inputVariants = {
    focused: {
      scale: 1.02,
      boxShadow: "0 8px 32px rgba(59, 130, 246, 0.15)",
      borderColor: "#3b82f6",
    },
    unfocused: {
      scale: 1,
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
      borderColor: "#e5e7eb",
    },
  };

  return (
    <div className="relative min-h-screen bg-white overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute bottom-3 -left-5 overflow-hidden lg:flex hidden opacity-30">
        <img src={Mask} alt="Mask" />
      </div>
      <div className="absolute top-3 -right-0 z-0 opacity-20">
        <img src={Mask} alt="Mask" />
      </div>

      {/* Floating Geometric Shapes */}
      <div className="absolute top-10 left-10 lg:flex hidden">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="w-20 h-20 bg-gray-100 rounded-full opacity-30"
        />
      </div>
      <div className="absolute top-14 right-1/3 lg:flex hidden">
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="w-14 h-14 bg-gray-100 rounded-full opacity-25"
        />
      </div>
      <div className="absolute bottom-32 right-0 lg:flex hidden">
        <motion.div
          animate={{ y: [-10, 10, -10] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="w-10 h-10 bg-gray-100 rounded-full opacity-20"
        />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="relative z-10 flex flex-col lg:flex-row justify-center min-h-screen px-4 sm:px-6 lg:px-8 py-16 max-w-7xl mx-auto"
      >
        {/* Left Content Section */}
        <motion.div
          variants={itemVariants}
          className="lg:w-1/2 lg:pr-8 mb-8 lg:mb-0 text-center lg:text-right"
        >
          <div className="space-y-4">
            <motion.h1
              className="font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl leading-tight text-[#14183E]"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <span className="bg-gradient-to-r from-[#14183E] via-blue-600 to-purple-600 bg-clip-text text-transparent !text-right">
                {faqData.heading ||
                  "Have A Question Or Looking To Book A Private Training?"}
              </span>
            </motion.h1>
            <motion.p
              className="text-lg sm:text-xl text-gray-600 font-medium leading-relaxed max-w-2xl mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {faqData.sub_heading ||
                "Please fill in our inquiry for more information"}
            </motion.p>
          </div>
        </motion.div>

        {/* Right Form Section */}
        <motion.div
          variants={itemVariants}
          className="lg:w-1/2 w-full flex justify-center lg:justify-start"
        >
          <div className="w-full max-w-2xl">
            <motion.div
              className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 lg:p-12"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              {/* Form Header */}
              <motion.div className="text-center mb-8" variants={itemVariants}>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-2">
                  {faqData?.form_heading || "Enter Your Details"}
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto"></div>
              </motion.div>

              {/* Form */}
              <motion.form
                onSubmit={handleSubmit}
                className="space-y-6"
                variants={containerVariants}
              >
                {/* First Row - First Name & Last Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.div variants={itemVariants} className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {faqData?.fields?.[0]?.placeholder || "First Name"}
                    </label>
                    <motion.input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField("firstName")}
                      onBlur={() => setFocusedField("")}
                      variants={inputVariants}
                      animate={
                        focusedField === "firstName" ? "focused" : "unfocused"
                      }
                      className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-all duration-300 text-gray-800 placeholder-gray-400 bg-white/80"
                      placeholder="Enter your first name"
                      required
                    />
                  </motion.div>

                  <motion.div variants={itemVariants} className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {faqData?.fields?.[1]?.placeholder || "Last Name"}
                    </label>
                    <motion.input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField("lastName")}
                      onBlur={() => setFocusedField("")}
                      variants={inputVariants}
                      animate={
                        focusedField === "lastName" ? "focused" : "unfocused"
                      }
                      className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-all duration-300 text-gray-800 placeholder-gray-400 bg-white/80"
                      placeholder="Enter your last name"
                      required
                    />
                  </motion.div>
                </div>

                {/* Second Row - Email & Location */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.div variants={itemVariants} className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {faqData?.fields?.[2]?.placeholder || "Email"}
                    </label>
                    <motion.input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField("")}
                      variants={inputVariants}
                      animate={
                        focusedField === "email" ? "focused" : "unfocused"
                      }
                      className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-all duration-300 text-gray-800 placeholder-gray-400 bg-white/80"
                      placeholder="Enter your email"
                      required
                    />
                  </motion.div>

                  <motion.div variants={itemVariants} className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {faqData?.fields?.[4]?.placeholder || "Location"}
                    </label>
                    <motion.input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      onFocus={() => setFocusedField("location")}
                      onBlur={() => setFocusedField("")}
                      variants={inputVariants}
                      animate={
                        focusedField === "location" ? "focused" : "unfocused"
                      }
                      className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-all duration-300 text-gray-800 placeholder-gray-400 bg-white/80"
                      placeholder="Enter your location"
                      required
                    />
                  </motion.div>
                </div>

                {/* Third Row - Phone */}
                <motion.div variants={itemVariants} className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {faqData?.fields?.[3]?.placeholder || "Phone"}
                  </label>
                  <motion.input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("phone")}
                    onBlur={() => setFocusedField("")}
                    variants={inputVariants}
                    animate={focusedField === "phone" ? "focused" : "unfocused"}
                    className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-all duration-300 text-gray-800 placeholder-gray-400 bg-white/80"
                    placeholder="Enter your phone number"
                    required
                  />
                </motion.div>

                {/* Fourth Row - Training Topics */}
                <motion.div variants={itemVariants} className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {faqData?.fields?.[5]?.placeholder || "Training Topics"}
                  </label>
                  <motion.input
                    type="text"
                    name="trainingTopics"
                    value={formData.trainingTopics}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("trainingTopics")}
                    onBlur={() => setFocusedField("")}
                    variants={inputVariants}
                    animate={
                      focusedField === "trainingTopics"
                        ? "focused"
                        : "unfocused"
                    }
                    className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-all duration-300 text-gray-800 placeholder-gray-400 bg-white/80"
                    placeholder="What training are you interested in?"
                    required
                  />
                </motion.div>

                {/* Fifth Row - Additional Notes */}
                <motion.div variants={itemVariants} className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {faqData?.fields?.[6]?.placeholder || "Additional Notes"}
                  </label>
                  <motion.textarea
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("additionalNotes")}
                    onBlur={() => setFocusedField("")}
                    variants={inputVariants}
                    animate={
                      focusedField === "additionalNotes"
                        ? "focused"
                        : "unfocused"
                    }
                    rows={4}
                    className="w-full px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-all duration-300 text-gray-800 placeholder-gray-400 bg-white/80 resize-none"
                    placeholder="Any additional information or special requirements..."
                  />
                </motion.div>

                {/* Submit Button */}
                <motion.div
                  variants={itemVariants}
                  className="flex justify-center pt-6"
                >
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full sm:w-auto px-12 py-4 bg-gradient-to-r from-[#B02526] to-[#8B1E1F]  hover:from-[#8B1E1F] hover:to-[#B02526] text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform"
                  >
                    <span className="flex items-center justify-center space-x-2">
                      <span>Submit</span>
                    </span>
                  </motion.button>
                </motion.div>
              </motion.form>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default FAQ;
